import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CRMHeader from '../../components/crm/CRMHeader';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import companySitesService, { CompanySite } from '../../services/crm/companySitesService';
import companySiteMembersService, { CompanySiteMember } from '../../services/crm/companySiteMembersService';
import { toast } from 'react-hot-toast';
import { useCrmRoleStore, isManager } from '../../store/crmRole.store';

const CompanySitesManager: React.FC = () => {
  const { companyId = '' } = useParams();
  const navigate = useNavigate();
  const { activeOrgId } = useCrmOrgStore();
  const [sites, setSites] = useState<CompanySite[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // create site form
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postal, setPostal] = useState('');
  const [country, setCountry] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  // members
  const [selectedSiteId, setSelectedSiteId] = useState<string>('');
  const [members, setMembers] = useState<CompanySiteMember[]>([]);
  const [inviteUserId, setInviteUserId] = useState('');
  const [inviteRole, setInviteRole] = useState<CompanySiteMember['role']>('staff');
  const { role } = useCrmRoleStore();

  useEffect(() => { document.title = 'CRM | Company Sites'; }, []);

  const loadSites = async () => {
    if (!activeOrgId || !companyId) return;
    setLoading(true); setError(null);
    try {
      const resp = await companySitesService.list(activeOrgId, companyId);
      setSites(resp.data || []);
      // If selection cleared, pick the primary or first
      if (!selectedSiteId) {
        const primary = (resp.data || []).find(s => s.is_primary);
        setSelectedSiteId(primary?.id || (resp.data?.[0]?.id || ''));
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async (siteId: string) => {
    if (!activeOrgId || !siteId) return;
    setError(null);
    try {
      const resp = await companySiteMembersService.list(activeOrgId, siteId);
      setMembers(resp.data || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load site members');
    }
  };

  useEffect(() => { loadSites(); /* eslint-disable-next-line */ }, [activeOrgId, companyId]);
  useEffect(() => { if (selectedSiteId) loadMembers(selectedSiteId); /* eslint-disable-next-line */ }, [selectedSiteId]);

  const createSite = async () => {
    if (!activeOrgId || !companyId || !name) { setError('Organization, Company and Name are required'); toast.error('Organization, Company and Name are required'); return; }
    setLoading(true); setError(null);
    try {
      await companySitesService.create({
        organization_id: activeOrgId,
        company_id: companyId,
        name,
        phone: phone || undefined,
        address_line1: address1 || undefined,
        address_line2: address2 || undefined,
        city: city || undefined,
        region: region || undefined,
        postal_code: postal || undefined,
        country: country || undefined,
        is_primary: isPrimary || undefined
      });
      setName(''); setPhone(''); setAddress1(''); setAddress2(''); setCity(''); setRegion(''); setPostal(''); setCountry(''); setIsPrimary(false);
      await loadSites();
      toast.success('Site created');
    } catch (e: any) {
      setError(e?.message || 'Failed to create site');
      toast.error('Failed to create site');
    } finally {
      setLoading(false);
    }
  };

  const setPrimary = async (id: string) => {
    if (!activeOrgId) return;
    setLoading(true); setError(null);
    try {
      await companySitesService.update(id, activeOrgId, { is_primary: true });
      await loadSites();
      toast.success('Primary site updated');
    } catch (e: any) {
      setError(e?.message || 'Failed to set primary');
      toast.error('Failed to set primary');
    } finally {
      setLoading(false);
    }
  };

  const addMember = async () => {
    if (!activeOrgId || !selectedSiteId || !inviteUserId) { setError('Organization, Site and User ID are required'); toast.error('Organization, Site and User ID are required'); return; }
    setLoading(true); setError(null);
    try {
      await companySiteMembersService.add(activeOrgId, selectedSiteId, inviteUserId, inviteRole);
      setInviteUserId(''); setInviteRole('staff');
      await loadMembers(selectedSiteId);
      toast.success('Member added');
    } catch (e: any) {
      setError(e?.message || 'Failed to add member');
      toast.error('Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (id: string) => {
    if (!activeOrgId || !selectedSiteId) { setError('Organization and Site are required'); toast.error('Organization and Site are required'); return; }
    setLoading(true); setError(null);
    try {
      await companySiteMembersService.remove(activeOrgId, selectedSiteId, id);
      await loadMembers(selectedSiteId);
      toast.success('Member removed');
    } catch (e: any) {
      setError(e?.message || 'Failed to remove member');
      toast.error('Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const selectedSite = useMemo(() => sites.find(s => s.id === selectedSiteId) || null, [sites, selectedSiteId]);

  return (
    <div className="space-y-4">
      <CRMHeader />
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Company Sites</h1>
        <button className="text-sm text-gray-600" onClick={() => navigate(-1)}>&larr; Back</button>
      </div>

      <div className="bg-white p-4 rounded shadow space-y-4">
        {!activeOrgId && (
          <div className="text-sm text-gray-600">Select or create an organization above to begin.</div>
        )}
        {isManager(role) && (
          <div>
            <h2 className="font-medium mb-2">Create Site</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input className="border rounded p-2" placeholder="Name" value={name} onChange={e => setName(e.target.value)} />
              <input className="border rounded p-2" placeholder="Phone (optional)" value={phone} onChange={e => setPhone(e.target.value)} />
              <label className="inline-flex items-center p-2"><input type="checkbox" className="mr-2" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} /> Primary</label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <input className="border rounded p-2" placeholder="Address line 1" value={address1} onChange={e => setAddress1(e.target.value)} />
              <input className="border rounded p-2" placeholder="Address line 2" value={address2} onChange={e => setAddress2(e.target.value)} />
              <input className="border rounded p-2" placeholder="City" value={city} onChange={e => setCity(e.target.value)} />
              <input className="border rounded p-2" placeholder="Region" value={region} onChange={e => setRegion(e.target.value)} />
              <input className="border rounded p-2" placeholder="Postal code" value={postal} onChange={e => setPostal(e.target.value)} />
              <input className="border rounded p-2" placeholder="Country" value={country} onChange={e => setCountry(e.target.value)} />
            </div>
            <div className="mt-3">
              <button className="bg-green-600 text-white rounded px-4" onClick={createSite} disabled={loading}>Create Site</button>
            </div>
          </div>
        )}

        <div className="pt-4 border-t">
          <h2 className="font-medium mb-2">Sites</h2>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sites.map(s => (
                <div key={s.id} className={`border rounded p-3 ${selectedSiteId === s.id ? 'ring-2 ring-blue-400' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{s.name} {s.is_primary && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Primary</span>}</div>
                      <div className="text-sm text-gray-600">{[s.address_line1, s.address_line2, s.city, s.region, s.postal_code, s.country].filter(Boolean).join(', ') || '—'}</div>
                    </div>
                    <div className="space-x-2">
                      <button className="text-blue-700" onClick={() => setSelectedSiteId(s.id)}>Select</button>
                      {isManager(role) && !s.is_primary && <button className="text-green-700" onClick={() => setPrimary(s.id)}>Set Primary</button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedSite && (
          <div className="pt-4 border-t">
            <h2 className="font-medium mb-2">Members for: {selectedSite.name}</h2>
            {isManager(role) && (
              <div className="grid grid-cols-1 md/grid-cols-3 gap-3">
                <input className="border rounded p-2" placeholder="User ID (UUID)" value={inviteUserId} onChange={e => setInviteUserId(e.target.value)} />
                <select className="border rounded p-2" value={inviteRole} onChange={e => setInviteRole(e.target.value as CompanySiteMember['role'])}>
                  <option value="staff">Staff</option>
                  <option value="manager">Manager</option>
                  <option value="visitor">Visitor</option>
                </select>
                <button className="bg-blue-600 text-white rounded px-4" onClick={addMember} disabled={loading}>Add</button>
              </div>
            )}
            <div className="mt-3">
              <table className="min-w-full border">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-2 text-left">User ID</th>
                    <th className="p-2 text-left">Role</th>
                    <th className="p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.id} className="border-t">
                      <td className="p-2">{m.user_id}</td>
                      <td className="p-2">{m.role}</td>
                      <td className="p-2 text-right">
                        {isManager(role) ? (
                          <button className="text-red-600" onClick={() => removeMember(m.id)} disabled={loading}>Remove</button>
                        ) : (
                          <span className="text-xs text-gray-400">No actions</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && <div className="text-red-600 text-sm">{error}</div>}
      </div>
    </div>
  );
};

export default CompanySitesManager;
